import React, { useCallback } from 'react';
import { DialogContent, DialogActions, Box } from '@mui/material';
import { ArrowForwardRounded } from '@mui/icons-material';
import { COLOR } from '@src/styles/color';
import {
  DUBBING_GUIDE_URL,
  PROJECT_GUIDE_URL,
  VOICE_CLONING_GUIDE_URL,
  VOICE_CLONING_GUIDE_VIDEO_URL,
  DUBBING_GUIDE_VIDEO_URL,
  PROJECT_GUIDE_VIDEO_URL,
  PREVIEW_GUIDE_URL,
  AI_PRONUNCIATION_CHECK_GUIDE_VIDEO_URL,
} from '@src/configs';
import { useTranslation } from 'react-i18next';
import { ONBOARDING_POPUP_TYPE } from '@src/constants';
import powerpointIcon from '@src/assets/icons/microsoft-powerpoint.svg';
import fileTextIcon from '@src/assets/icons/file-text.svg';
import closedCaptioningIcon from '@src/assets/icons/closed-captioning.svg';
import fileAudioIcon from '@src/assets/icons/file-audio.svg';
import videoIcon from '@src/assets/icons/video.svg';
import linkIcon from '@src/assets/icons/link-simple-horizontal.svg';
import Typography from '../Typography';
import { StyledDialog } from './index.style';
import CommonButton from '../Button';

// Shared Typography variants carry -0.4px tracking; the Educators popup is
// drawn without it. Overriding per usage keeps the other popups untouched.
const NO_LETTER_SPACING = { letterSpacing: 0 };

const openInNewTab = (url) => window.open(url, '_blank');

// Vbee for Educators tools, shown two rows of three. Each one renders as
// "from -> to" with an arrow icon between, so the terms are translated on
// their own instead of being baked into one string per pair.
const EDUCATOR_FEATURES = [
  {
    icon: powerpointIcon,
    from: 'educatorsTermSlide',
    to: 'educatorsTermVideo',
  },
  { icon: fileTextIcon, from: 'educatorsTermText', to: 'educatorsTermAudio' },
  {
    icon: closedCaptioningIcon,
    from: 'educatorsTermSubtitle',
    to: 'educatorsTermAudio',
  },
  {
    icon: fileAudioIcon,
    from: 'educatorsTermAudio',
    to: 'educatorsTermSubtitle',
  },
  { icon: videoIcon, from: 'educatorsTermVideo', to: 'educatorsTermSubtitle' },
  { icon: linkIcon, from: 'educatorsTermUrl', to: 'educatorsTermAudio' },
];

const OnboardingPopup = ({ open, onClose, type }) => {
  const { t } = useTranslation();
  const renderActionComponent = useCallback(
    (popupType, guideUrl) => {
      if (
        popupType === ONBOARDING_POPUP_TYPE.PREVIEW ||
        popupType === ONBOARDING_POPUP_TYPE.AI_PRONUNCIATION_CHECK
      ) {
        return (
          <CommonButton
            variant="contained"
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '10px 16px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            onClick={onClose}
          >
            {t('gotIt')}
          </CommonButton>
        );
      }
      return (
        <>
          <CommonButton
            variant="outlined"
            className="dialog-actions-button"
            sx={{
              height: '40px',
              border: `1px solid ${COLOR.secondary[16]}`,
              padding: '10px 16px',
              borderRadius: '8px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.indigo.default,
            }}
            onClick={() => openInNewTab(guideUrl)}
          >
            {t('onboardingViewGuideDetail')}
          </CommonButton>
          <CommonButton
            variant="contained"
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '10px 16px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            onClick={onClose}
          >
            {t('startExperience')}
          </CommonButton>
        </>
      );
    },
    [t, openInNewTab, onClose],
  );

  // Welcome popup for Vbee for Educators — a banner over the tool list, laid out
  // differently from the guide-video popups, so it renders on its own.
  if (type === ONBOARDING_POPUP_TYPE.EDUCATORS) {
    return (
      <StyledDialog open={open} onClose={onClose} className="educators-popup">
        <DialogContent className="dialog-content">
          <Box className="educators-banner-section">
            <Box className="educators-banner">
              <div className="educators-beta">
                <Typography
                  variant="body-12-medium"
                  color={COLOR.white}
                  style={NO_LETTER_SPACING}
                >
                  {t('betaV2')}
                </Typography>
              </div>
              <div className="educators-banner-text">
                <Typography variant="heading-40-regular" color={COLOR.white}>
                  {t('educatorsPopupWelcome')}
                </Typography>
                <Typography variant="heading-48-bold" color={COLOR.white}>
                  {t('vbeeForEducators')}
                </Typography>
              </div>
            </Box>
          </Box>
          <Box className="educators-body">
            <div className="educators-intro">
              <Typography
                variant="body-24-semibold"
                color={COLOR.darkGray}
                style={NO_LETTER_SPACING}
              >
                {t('vbeeForEducators')}
              </Typography>
              <Typography variant="body-14-regular" color={COLOR.label2[64]}>
                {t('educatorsPopupSubtitle')}
              </Typography>
            </div>
            <div className="educators-features">
              {EDUCATOR_FEATURES.map(({ icon, from, to }) => (
                <div key={`${from}-${to}`} className="educators-feature">
                  <img src={icon} alt="" className="educators-feature-icon" />
                  <div className="educators-feature-label">
                    <Typography
                      variant="body-14-regular"
                      color={COLOR.midSlate}
                      style={NO_LETTER_SPACING}
                    >
                      {t(from)}
                    </Typography>
                    <ArrowForwardRounded className="educators-feature-arrow" />
                    <Typography
                      variant="body-14-regular"
                      color={COLOR.midSlate}
                      style={NO_LETTER_SPACING}
                    >
                      {t(to)}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </Box>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CommonButton
            variant="contained"
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 16px',
              '& .MuiTypography-root': NO_LETTER_SPACING,
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            onClick={onClose}
          >
            {t('startExperience')}
          </CommonButton>
        </DialogActions>
      </StyledDialog>
    );
  }

  const TYPE_ONBOARDING = {
    VOICE_CLONING: {
      isNew: true,
      title: 'voiceCloningLabel',
      content: 'onboardingVoiceCloningContent',
      videoUrl: VOICE_CLONING_GUIDE_VIDEO_URL,
      actionComponent: renderActionComponent(
        ONBOARDING_POPUP_TYPE.VOICE_CLONING,
        VOICE_CLONING_GUIDE_URL,
      ),
    },
    DUBBING: {
      isNew: false,
      title: 'newDubbingName',
      content: 'onboardingDubbingContent',
      videoUrl: DUBBING_GUIDE_VIDEO_URL,
      actionComponent: renderActionComponent(
        ONBOARDING_POPUP_TYPE.DUBBING,
        DUBBING_GUIDE_URL,
      ),
    },
    PROJECT: {
      isNew: false,
      title: 'project',
      content: 'onboardingProjectContent',
      videoUrl: PROJECT_GUIDE_VIDEO_URL,
      actionComponent: renderActionComponent(
        ONBOARDING_POPUP_TYPE.PROJECT,
        PROJECT_GUIDE_URL,
      ),
    },
    PREVIEW: {
      isNew: false,
      title: 'preview',
      content: 'onboardingPreviewContent',
      videoUrl: PREVIEW_GUIDE_URL,
      actionComponent: renderActionComponent(
        ONBOARDING_POPUP_TYPE.PREVIEW,
        PREVIEW_GUIDE_URL,
      ),
    },
    AI_PRONUNCIATION_CHECK: {
      isNew: false,
      isBeta: true,
      title: 'aiEditorPronunciationCheck',
      content: 'onboardingAiEditorContent',
      videoUrl: AI_PRONUNCIATION_CHECK_GUIDE_VIDEO_URL,
      actionComponent: renderActionComponent(
        ONBOARDING_POPUP_TYPE.AI_PRONUNCIATION_CHECK,
      ),
    },
  };
  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogContent className="dialog-content">
        <Box className="video-container">
          <iframe
            src={TYPE_ONBOARDING[type]?.videoUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </Box>
        <Box className="content-container">
          <Box className="content-container-header">
            <Typography variant="heading-32-bold" color={COLOR.darkGray}>
              {t(TYPE_ONBOARDING[type]?.title)}
            </Typography>
            {TYPE_ONBOARDING[type]?.isNew && (
              <div className="new-label">
                <Typography variant="body-12-medium" color={COLOR.white}>
                  {t('new')}
                </Typography>
              </div>
            )}
            {TYPE_ONBOARDING[type]?.isBeta && (
              <div className="beta-label">
                <Typography variant="body-12-medium" color={COLOR.blueV2}>
                  {t('betaV2')}
                </Typography>
              </div>
            )}
          </Box>
          <Typography variant="body-16-regular" color={COLOR.label2[64]}>
            {t(TYPE_ONBOARDING[type]?.content)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className="dialog-actions">
        {TYPE_ONBOARDING[type]?.actionComponent}
      </DialogActions>
    </StyledDialog>
  );
};
export default OnboardingPopup;
